import React from "react";
//#region types
import { DateRangeFieldProps } from "models/propTypes/wintFormPropsTypes";
import { DateRange } from "models/formTypes/util";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import { FormControl, Grid } from "@mui/material";
import SimpleDatePicker from "../../datePickers/simpleDatePicker";
import MessageBubble from "../../messages/messageBubble";
import Label from "../formLayout/label";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import { TranslationKeys } from "translations";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const DateRangeField: React.FC<DateRangeFieldProps> = (props) => {
  const {
    name,
    value,
    setValue,
    onBlur,
    error,
    hasError,
    label,
    startDateLabel,
    endDateLabel,
    labelRightContent,
    onDateRangeChange,
    minDate,
    maxDate,
    startDateInitialFocus,
    endDateInitialFocus,
    customMessage,
    hideMessageBubble,
    messageBubblePlacement,
    descriptionText,
    disabled,
    optional,
    startDisabled,
    endDisabled,
  } = props;

  const startError = typeof error === "object" ? error?.start : undefined;
  const endError = typeof error === "object" ? error?.end : undefined;
  const objectLevelError = typeof error === "string" ? error : undefined;
  const displayError = startError || endError || objectLevelError;
  const hasObjectLevelError = hasError && !!objectLevelError;
  const hasStartError = hasError && (!!startError || hasObjectLevelError);
  const hasEndError = hasError && (!!endError || hasObjectLevelError);

  let messageType = StyleEnums.DisplayType.None;
  let message: string | React.ReactElement | undefined;

  if (hasError && displayError) {
    message =
      typeof displayError === "string"
        ? LocalizationHelperInstance.getFormattedMessage(displayError, displayError)
        : displayError;
    messageType = StyleEnums.DisplayType.Danger;
  } else if (customMessage) {
    message = customMessage.message;
    messageType = customMessage.type;
  }

  const isValidOrEmpty = (date?: Date): boolean =>
    date === undefined || (date instanceof Date && !isNaN(date.getTime()));

  const handleStartDateChange = (date?: Date) => {
    if (!isValidOrEmpty(date)) return;

    const newRange: DateRange = { start: date, end: value?.end };
    setValue(newRange);
    if (onDateRangeChange) {
      onDateRangeChange(newRange);
    }
  };

  const handleEndDateChange = (date?: Date) => {
    if (!isValidOrEmpty(date)) return;

    const newRange: DateRange = { start: value?.start, end: date };
    setValue(newRange);
    if (onDateRangeChange) {
      onDateRangeChange(newRange);
    }
  };

  const actualLabel = typeof label === "string" ? LocalizationHelperInstance.getFormattedMessage(label, label) : label;
  const actualStartDateLabel =
    typeof startDateLabel === "string"
      ? LocalizationHelperInstance.getFormattedMessage(startDateLabel, startDateLabel)
      : startDateLabel;
  const actualEndDateLabel =
    typeof endDateLabel === "string"
      ? LocalizationHelperInstance.getFormattedMessage(endDateLabel, endDateLabel)
      : endDateLabel;

  return (
    <Grid container item spacing={1} direction="column" className={clsx(cssExports.formControl)}>
      <Grid
        item
        container
        xs
        direction="column"
        className={clsx(cssExports.formControlLabelContainer, cssExports.formFieldMessageAttachElemTop)}
      >
        {label && (
          <Grid item container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item>
              <Label className={cssExports.formLabelTop}>{actualLabel}</Label>
            </Grid>
            {labelRightContent && <Grid item>{labelRightContent}</Grid>}
          </Grid>
        )}

        {!hideMessageBubble && (
          <MessageBubble placement={messageBubblePlacement ?? "top"} message={message} type={messageType} />
        )}

        <Grid item container direction="row" spacing={1}>
          <Grid item xs={6}>
            <FormControl fullWidth className={cssExports.inputFormControl}>
              <SimpleDatePicker
                disabled={disabled || startDisabled}
                inputFormat="yyyy-MM-dd"
                label={actualStartDateLabel}
                overridePlaceholder={`${actualStartDateLabel}${
                  optional
                    ? `${actualStartDateLabel ? " " : ""}(${LocalizationHelperInstance.getFormattedMessage(
                        TranslationKeys.Common.Optional
                      )})`
                    : ""
                }`}
                value={value?.start}
                defaultCalendarMonth={startDateInitialFocus}
                error={hasStartError}
                name={`${name}.start`}
                onChange={handleStartDateChange}
                onBlur={onBlur}
                minDate={minDate}
                maxDate={maxDate}
                data-testid={`${name}StartDateInput`}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth className={cssExports.inputFormControl}>
              <SimpleDatePicker
                disabled={disabled || endDisabled}
                inputFormat="yyyy-MM-dd"
                label={actualEndDateLabel}
                overridePlaceholder={`${actualEndDateLabel}${
                  optional
                    ? `${actualEndDateLabel ? " " : ""}(${LocalizationHelperInstance.getFormattedMessage(
                        TranslationKeys.Common.Optional
                      )})`
                    : ""
                }`}
                value={value?.end}
                defaultCalendarMonth={endDateInitialFocus}
                error={hasEndError}
                name={`${name}.end`}
                onChange={handleEndDateChange}
                onBlur={onBlur}
                minDate={minDate}
                maxDate={maxDate}
                data-testid={`${name}EndDateInput`}
              />
            </FormControl>
          </Grid>
        </Grid>

        {descriptionText && (
          <Grid item className={clsx(cssExports.formControlDescription, cssExports.verticalFormDescription)}>
            {typeof descriptionText === "string"
              ? LocalizationHelperInstance.getFormattedMessage(descriptionText, descriptionText)
              : descriptionText}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default DateRangeField;
